/**
 * 剧本标准格式 · 与 backend/internal/pkg/scriptformat 对齐
 *
 * - hollywood: Fountain + Final Draft 页边距（US Letter）
 * - cn: 场号 + 内景/外景 · 地点 - 时间（A4 常用版式）
 */

import { parseFountainToElements } from '@/features/studio/script/fountain-parse'
import type { ScriptElement } from '@/features/studio/script/script-elements'
import type { ScriptConvention } from '@/features/studio/script/useScriptFormatPrefs'

export type { ScriptConvention }

const CN_SCENE_PARTS =
  /^(\d+)\.\s*(内景|外景|场景)\s*[·•·\s]+\s*(.+?)\s*[-–—]\s*(.+)$/
const CN_SCENE_BARE = /^(内景|外景|场景)\s+(.+?)\s+(日|夜|晨|昏|傍晚|清晨|白天|夜晚)$/
const HOLLYWOOD_SCENE = /^(INT\.|EXT\.|INT\.\/EXT\.|I\/E\.)\s*(.+?)\s*[-–—]\s*(.+)$/i
const SCENE_HEADING_HOLLYWOOD =
  /^(?:\.|INT\.|EXT\.|INT\.\/EXT\.|I\/E\.|E\/I\.|INT\/EXT\.)/i

function mapCnTimeOfDay(raw: string): string {
  switch (raw.trim()) {
    case '日':
    case '白天':
    case '晨':
    case '清晨':
      return 'DAY'
    case '夜':
    case '夜晚':
    case '昏':
    case '傍晚':
      return 'NIGHT'
    default:
      return raw.toUpperCase()
  }
}

function mapHollywoodTimeOfDay(raw: string): string {
  const up = raw.trim().toUpperCase()
  if (up === 'DAY' || up === 'MORNING') return '日'
  if (up === 'NIGHT' || up === 'EVENING') return '夜'
  if (up.includes('NIGHT')) return '夜'
  return '日'
}

function formatSceneHeading(content: string, fallbackNo: number, convention: ScriptConvention): string {
  const trimmed = content.trim()
  if (convention === 'hollywood') {
    if (HOLLYWOOD_SCENE.test(trimmed) || SCENE_HEADING_HOLLYWOOD.test(trimmed)) {
      return trimmed.toUpperCase()
    }
    const m = CN_SCENE_PARTS.exec(trimmed)
    if (m) {
      const prefix = m[2] === '外景' ? 'EXT.' : 'INT.'
      return `${prefix} ${m[3]!.trim()} - ${mapCnTimeOfDay(m[4]!)}`.toUpperCase()
    }
    return trimmed.toUpperCase()
  }

  const numbered = CN_SCENE_PARTS.exec(trimmed)
  if (numbered) {
    return `${numbered[1]}. ${numbered[2]} · ${numbered[3]!.trim()} - ${numbered[4]!.trim()}`
  }
  const dotted = trimmed.match(/^(内景|外景|场景)\s*·\s*(.+?)\s*[-–—]\s*(.+)$/)
  if (dotted) {
    return `${fallbackNo}. ${dotted[1]} · ${dotted[2]!.trim()} - ${dotted[3]!.trim()}`
  }
  const bare = CN_SCENE_BARE.exec(trimmed)
  if (bare) {
    return `${fallbackNo}. ${bare[1]} · ${bare[2]!.trim()} - ${bare[3]!.trim()}`
  }
  if (/^\d+\.\s*(?:内景|外景|场景)/.test(trimmed)) return trimmed
  if (SCENE_HEADING_HOLLYWOOD.test(trimmed)) {
    const m = HOLLYWOOD_SCENE.exec(trimmed.toUpperCase())
    if (m) {
      const kind = m[1]!.startsWith('EXT') ? '外景' : '内景'
      return `${fallbackNo}. ${kind} · ${m[2]!.trim()} - ${mapHollywoodTimeOfDay(m[3]!)}`
    }
  }
  return `${fallbackNo}. ${trimmed}`
}

function formatCharacter(content: string, convention: ScriptConvention): string {
  const t = content.trim()
  return convention === 'hollywood' ? t.toUpperCase() : t
}

function formatParenthetical(content: string, convention: ScriptConvention): string {
  const inner = content.replace(/^[\(（]|[\)）]$/g, '').trim()
  return convention === 'cn' ? `（${inner}）` : `(${inner})`
}

function formatTransition(content: string, convention: ScriptConvention): string {
  const t = content.trim()
  if (convention === 'hollywood') {
    const up = t.toUpperCase()
    if (/^(?:CUT TO:|SMASH CUT TO:|FADE OUT\.|FADE IN:|FADE TO:|DISSOLVE TO:)/i.test(up)) return up
    if (t === '切至：' || t === '切至:' || t === '切至') return 'CUT TO:'
    return up
  }
  if (/^(?:CUT TO:|FADE OUT\.)/i.test(t)) return '切至：'
  if (t.endsWith('：') || t.endsWith(':')) return t
  return '切至：'
}

function formatShot(content: string, convention: ScriptConvention): string {
  const inner = content.trim().replace(/^【|】$/g, '')
  if (convention === 'hollywood') {
    const up = inner.toUpperCase()
    if (/^(?:ANGLE ON|CLOSE ON|POV|INSERT|WIDE ON|MONTAGE)\b/.test(up)) return up
    return `ANGLE ON — ${up}`
  }
  if (/^镜头[·：]/.test(inner)) return inner
  return `镜头 · ${inner}`
}

/** 按约定将结构化元素序列化为标准 Fountain 正文。 */
export function serializeElementsStandard(
  elements: ScriptElement[],
  convention: ScriptConvention = 'cn',
): string {
  const blocks: string[] = []
  let sceneCounter = 0

  for (const el of elements) {
    const content = el.content.trim()
    if (!content && el.type !== 'action') continue

    switch (el.type) {
      case 'scene_heading':
        sceneCounter += 1
        blocks.push(formatSceneHeading(content, sceneCounter, convention))
        blocks.push('')
        break
      case 'action':
        if (content) {
          blocks.push(content)
          blocks.push('')
        }
        break
      case 'character':
        blocks.push(formatCharacter(content, convention))
        break
      case 'parenthetical':
        blocks.push(formatParenthetical(content, convention))
        break
      case 'dialogue':
        if (content) {
          blocks.push(content)
          blocks.push('')
        }
        break
      case 'transition':
        blocks.push(formatTransition(content, convention))
        blocks.push('')
        break
      case 'shot':
        blocks.push(formatShot(content, convention))
        blocks.push('')
        break
      case 'note':
        if (content) {
          blocks.push(`[[ ${content} ]]`)
          blocks.push('')
        }
        break
      default:
        break
    }
  }

  return `${blocks.join('\n').replace(/\n{3,}/g, '\n\n').trim()}\n`
}

/** 解析后按约定规范化正文（保存/导出前调用）。 */
export function normalizeScriptBody(body: string, convention: ScriptConvention): string {
  const trimmed = body.replace(/\r\n/g, '\n').trim()
  if (!trimmed) return ''
  return serializeElementsStandard(parseFountainToElements(trimmed, convention), convention)
}

export function normalizeConvention(raw: string | undefined | null): ScriptConvention {
  const v = (raw ?? '').trim().toLowerCase()
  if (v === 'hollywood' || v === 'fountain' || v === 'fd' || v === 'us') return 'hollywood'
  return 'cn'
}
